import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import ReactCountryFlag from 'react-country-flag'
import { getProcessedCountryData } from '../../utils/countryData'

/**
 * Custom Country Input Component for editing
 * Provides a dropdown selector with country flags and search functionality
 */
const CountryInput = ({
  value,
  onChange,
  isRequired = false,
  disabled = false,
  placeholder = 'Select a country',
  hasError = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [showCountrySelect, setShowCountrySelect] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const dropdownRef = useRef(null)

  // Get processed country data with flags and translated names
  const countryData = useMemo(() => {
    return getProcessedCountryData()
  }, [])

  // Sort countries alphabetically
  const sortedCountries = useMemo(() => {
    return countryData.sort((a, b) => a.name.localeCompare(b.name))
  }, [countryData])

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return sortedCountries

    const searchLower = searchTerm.toLowerCase()
    return sortedCountries
      .filter(
        (country) =>
          country.name.toLowerCase().includes(searchLower) ||
          country.dialCode.includes(searchTerm) ||
          country.code.toLowerCase().includes(searchLower),
      )
      .sort((a, b) => {
        const aNameLower = a.name.toLowerCase()
        const bNameLower = b.name.toLowerCase()

        if (aNameLower === searchLower) return -1
        if (bNameLower === searchLower) return 1

        if (aNameLower.startsWith(searchLower)) return -1
        if (bNameLower.startsWith(searchLower)) return 1

        const aIndex = aNameLower.indexOf(searchLower)
        const bIndex = bNameLower.indexOf(searchLower)
        if (aIndex !== bIndex) return aIndex - bIndex

        return aNameLower.localeCompare(bNameLower)
      })
  }, [sortedCountries, searchTerm])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountrySelect(false)
        setSearchTerm('')
      }
    }

    if (showCountrySelect) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCountrySelect])

  // Extract country from saved value
  const extractCountryFromValue = (savedValue) => {
    if (!savedValue || typeof savedValue !== 'string') {
      return null
    }

    // Handle format: +{dialCode} {countryName}
    const match = savedValue.match(/^\+(\d+)\s+(.+)$/)
    if (match) {
      const dialCode = match[1]
      const countryName = match[2]

      // Find country by dial code and name
      const country = sortedCountries.find(
        (c) => c.dialCode === dialCode && c.name === countryName,
      )
      return country || null
    }

    // Legacy format: only country name (no dial code)
    const trimmed = savedValue.trim()
    if (trimmed) {
      const countryByName = sortedCountries.find(
        (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
      )
      if (countryByName) return countryByName
    }

    return null
  }

  // Initialize selected country from saved value
  useEffect(() => {
    if (sortedCountries.length > 0 && !isInitialized) {
      const extractedCountry = extractCountryFromValue(value)

      if (extractedCountry) {
        setSelectedCountry(extractedCountry)
        // Normalize legacy name-only values to '+{dialCode} {name}' so validation passes
        try {
          const hasPlusFormat =
            typeof value === 'string' && /^\+\d+\s+.+$/.test(value.trim())
          if (!hasPlusFormat && typeof onChange === 'function') {
            const normalized = `+${extractedCountry.dialCode} ${extractedCountry.name}`
            onChange(normalized)
          }
        } catch (_) {}
      } else {
        // Default to US if no saved value
        const defaultCountry =
          sortedCountries.find((c) => c.code === 'us') || sortedCountries[0]
        setSelectedCountry(defaultCountry)
      }
      setIsInitialized(true)
    }
  }, [value, sortedCountries, isInitialized, onChange])

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption)
    setShowCountrySelect(false)
    setSearchTerm('')

    // Store the full format for submission
    const fullValue = `+${selectedOption.dialCode} ${selectedOption.name}`
    onChange(fullValue)
  }

  const handleDropdownToggle = () => {
    if (!disabled) {
      setShowCountrySelect(!showCountrySelect)
      setSearchTerm('')
    }
  }

  return (
    <div className='d-flex align-items-center'>
      {/* Country selector dropdown */}
      <div
        className='position-relative me-2'
        style={{ minWidth: '80px' }}
        ref={dropdownRef}>
        <button
          type='button'
          className={classNames(
            'form-control d-flex align-items-center justify-content-between',
            {
              'is-invalid': hasError,
            },
          )}
          onClick={handleDropdownToggle}
          disabled={disabled}
          style={{
            background: disabled ? '#f8f9fa' : '#fff',
            height: '42px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            padding: '8px 12px',
            borderRadius: '4px',
          }}>
          {selectedCountry ? (
            <>
              <ReactCountryFlag
                countryCode={(selectedCountry?.code || 'us').toUpperCase()}
                svg
                className='me-2'
                style={{
                  width: '1.25em',
                  height: '1.25em',
                  borderRadius: '30%',
                }}
                aria-label={(selectedCountry?.code || 'us').toUpperCase()}
              />
            </>
          ) : (
            <span style={{ color: '#666', fontSize: '14px' }}>Select</span>
          )}
          <svg width='16' height='16' viewBox='0 0 16 16' className='ms-2'>
            <polyline
              points='4,6 8,10 12,6'
              fill='none'
              stroke='#000'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>

        {/* Country dropdown */}
        {showCountrySelect && (
          <div
            className='position-absolute bg-white border rounded'
            style={{
              top: '100%',
              left: 0,
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              marginTop: '2px',
              minWidth: '280px',
              width: 'max-content',
            }}>
            {/* Search input */}
            <div
              className='p-2 border-bottom'
              style={{ backgroundColor: '#f8f9fa' }}>
              <input
                type='text'
                placeholder='Search countries...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='form-control form-control-sm'
                style={{
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  padding: '6px 10px',
                }}
                autoFocus
              />
            </div>

            {/* Country list */}
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className='d-flex align-items-center px-3 py-2 cursor-pointer'
                  onClick={() => handleCountryChange(country)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    minWidth: '280px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                  }}>
                  <ReactCountryFlag
                    countryCode={country.code.toUpperCase()}
                    svg
                    className='me-2'
                    style={{
                      width: '1.25em',
                      height: '1.25em',
                      borderRadius: '30%',
                    }}
                    aria-label={country.code.toUpperCase()}
                  />
                  <span
                    className='flex-grow-1 text-truncate'
                    style={{ fontSize: '14px' }}>
                    {country.name}
                  </span>
                  <span
                    className='ms-2'
                    style={{ fontSize: '14px', color: '#666' }}>
                    +{country.dialCode}
                  </span>
                </div>
              ))
            ) : (
              <div
                className='p-3 text-center text-muted'
                style={{ fontSize: '14px' }}>
                No countries found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Country name display (read-only) */}
      <input
        className='form-control flex-grow-1'
        value={selectedCountry ? selectedCountry.name : ''}
        readOnly
        disabled
        style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #000',
          color: '#000',
          height: '42px',
          fontSize: '14px',
        }}
      />
    </div>
  )
}

export default CountryInput
